import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AcademicCapIcon,
  ArchiveBoxXMarkIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ClockIcon,
  GlobeAltIcon,
  PencilSquareIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Card, Container, EmptyState, InfoBox, Input, Loading, Modal, PageHeader, RowAction, Select, Table, Tabs, Tag, Textarea } from "../../ui";
import { errorMessageOf, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { countries } from "../../data/countries";
import { ACADEMIC_YEARS_KEY } from "../../../hooks/useAcademicYears";
import { OPTIONS_KEY, SCHOLARSHIPS_KEY, countryName, currencyName } from "../model";
import { formatDateTime } from "../review/model";

/*
 * Settings (decision of 16 October 2026): what the Scholarship Admin configures instead of lists
 * fixed in the screens. Forms offer only these values; values already saved stay as they are.
 */

const SETTINGS_KEY = [...SCHOLARSHIPS_KEY, "settings"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((label, i) => ({ value: String(i + 1), label }));
const DAYS = Array.from({ length: 28 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));

const TABS = [
  { value: "years", label: "Academic years", icon: <CalendarDaysIcon className="h-4 w-4" /> },
  { value: "countries", label: "Destination countries", icon: <GlobeAltIcon className="h-4 w-4" /> },
  { value: "currencies", label: "Currencies", icon: <BanknotesIcon className="h-4 w-4" /> },
  { value: "levels", label: "Education levels", icon: <AcademicCapIcon className="h-4 w-4" /> },
  { value: "fields", label: "Fields of study", icon: <BookOpenIcon className="h-4 w-4" /> },
];

const useSetting = (path, select = (r) => r.data) =>
  useQuery({ queryKey: [...SETTINGS_KEY, path], queryFn: async () => select(await scholarshipsApi.get(`admin/settings${path}`)) });

// ------------------------------------------------------------------ academic years

const yearStatus = (y) => (y.current ? <Tag color="green">Current</Tag> : y.past ? <Tag color="gray">Past</Tag> : <Tag color="blue">Upcoming</Tag>);

function AcademicYearsTab() {
  const queryClient = useQueryClient();
  const settings = useSetting("", (r) => r);
  const years = useSetting("/academic-years");
  const [start, setStart] = useState(null);
  const [busy, setBusy] = useState(false);

  const current = settings.data?.data;
  useEffect(() => {
    if (current && !start) setStart({ month: String(current.academicYearStartMonth), day: String(current.academicYearStartDay) });
  }, [current, start]);

  if (settings.isPending || years.isPending) return <Loading label="Loading the academic years…" />;
  if (settings.isError || years.isError) return <EmptyState title="Could not load the academic years" onRefresh={() => Promise.all([settings.refetch(), years.refetch()])} />;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
    queryClient.invalidateQueries({ queryKey: ACADEMIC_YEARS_KEY });
  };
  const changed = start && (start.month !== String(current.academicYearStartMonth) || start.day !== String(current.academicYearStartDay));
  const code = current.currentAcademicYearCode;

  const saveStart = async () => {
    setBusy(true);
    try {
      await scholarshipsApi.put("admin/settings", { academicYearStartMonth: Number(start.month), academicYearStartDay: Number(start.day) }, { ifMatch: settings.data.etag });
      toast.success("Start of the academic year saved");
      refresh();
    } catch (error) {
      console.error("Failed to save the start of the academic year:", error);
      toast.error(errorMessageOf(error, "Failed to save. Please reload and try again."));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: "name", label: "Academic year", accessor: (y) => y.name, render: (y) => <span className="font-medium text-slate-800">{y.name}</span> },
    { key: "status", label: "Status", accessor: (y) => (y.current ? "Current" : y.past ? "Past" : "Upcoming"), render: yearStatus },
  ];

  return (
    <div className="flex flex-col gap-6">
      <Card title="When a new academic year starts" subtitle="On this day (Kigali time) the next academic year becomes the current one. New scholarships can only be for the current year or the next one.">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <Select label="Month" options={MONTHS} value={start?.month ?? ""} onChange={(e) => setStart((s) => ({ ...s, month: e.target.value }))} className="sm:w-48" disabled={busy} />
            <Select label="Day" options={DAYS} value={start?.day ?? ""} onChange={(e) => setStart((s) => ({ ...s, day: e.target.value }))} className="sm:w-28" disabled={busy} />
            <Button onClick={saveStart} disabled={!changed || busy} loading={busy && changed}>
              Save
            </Button>
          </div>
          {current.currentAcademicYear ? (
            <p className="text-sm text-slate-600">
              The current academic year is <span className="font-semibold text-slate-800">{current.currentAcademicYear.name}</span>.
            </p>
          ) : (
            <InfoBox>
              Today's academic year ({code}-{code + 1}) has not been added yet. It is added automatically tonight.
            </InfoBox>
          )}
        </div>
      </Card>

      <Card title="Academic years" subtitle="The current and next academic years are added automatically when a new year starts.">
        <div className="flex flex-col gap-4">
          <Table columns={columns} data={years.data} rowKey={(y) => y.id} searchable={false} exportable={false} emptyTitle="No academic years yet" />
        </div>
      </Card>
    </div>
  );
}

// ------------------------------------------------------------------ countries and currencies

/** A searchable list of codes to tick; saving replaces the whole list offered. */
function CodeChecklist({ path, all, labelOf, noun, help }) {
  const queryClient = useQueryClient();
  const offered = useSetting(path);
  const [chosen, setChosen] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (offered.data && chosen === null) setChosen(new Set(offered.data));
  }, [offered.data, chosen]);

  // Every code we know, plus any offered that we have no name for
  const codes = useMemo(() => [...new Set([...all, ...(offered.data ?? [])])].sort((a, b) => labelOf(a).localeCompare(labelOf(b))), [all, offered.data, labelOf]);
  if (offered.isPending || chosen === null) return <Loading label={`Loading the ${noun}…`} />;
  if (offered.isError) return <EmptyState title={`Could not load the ${noun}`} onRefresh={offered.refetch} />;

  const shown = codes.filter((c) => `${c} ${labelOf(c)}`.toLowerCase().includes(search.trim().toLowerCase()));
  const changed = chosen.size !== offered.data.length || offered.data.some((c) => !chosen.has(c));
  const toggle = (code) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await scholarshipsApi.put(`admin/settings${path}`, { codes: [...chosen] });
      queryClient.setQueryData([...SETTINGS_KEY, path], data);
      queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
      setChosen(new Set(data));
      toast.success(`${data.length} ${noun} offered`);
    } catch (error) {
      console.error(`Failed to save the ${noun}:`, error);
      toast.error(problemOf(error)?.code === "at-least-one" ? `Keep at least one of the ${noun}.` : errorMessageOf(error, "Failed to save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title={`${chosen.size} of ${codes.length} ${noun} offered`} subtitle={help}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <Input label="Search" placeholder={`Search the ${noun}`} value={search} onChange={(e) => setSearch(e.target.value)} className="sm:w-72" />
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={() => setChosen(new Set(offered.data))} disabled={!changed || busy}>
              Discard changes
            </Button>
            <Button onClick={save} disabled={!changed || chosen.size === 0 || busy} loading={busy}>
              Save
            </Button>
          </div>
        </div>
        {chosen.size === 0 && <p className="text-sm text-red-600">Keep at least one of the {noun}.</p>}
        <div className="grid max-h-[28rem] grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((code) => (
            <label key={code} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50">
              <input type="checkbox" className="h-4 w-4 accent-brand" checked={chosen.has(code)} onChange={() => toggle(code)} disabled={busy} />
              <span className="text-slate-800">{labelOf(code)}</span>
              <span className="text-xs text-slate-400">{code}</span>
            </label>
          ))}
        </div>
      </div>
    </Card>
  );
}

const COUNTRY_CODES = countries.map((c) => c.code);
const currencyCodes = () => {
  try {
    return Intl.supportedValuesOf("currency");
  } catch {
    return [];
  }
};

// ------------------------------------------------------------------ levels and fields of study

const MIN_REASON = 10;
const ACTIONS = { ADDED: "Added", RENAMED: "Renamed", RETIRED: "Retired", RESTORED: "Restored" };

/** Rename (new name + reason), retire or restore (reason), all recorded in the item's history. */
function ChangeDialog({ change, noun, busy, error, onClose, onSave }) {
  const [name, setName] = useState(change?.item.name ?? "");
  const [reason, setReason] = useState("");
  if (!change) return null;
  const { kind, item } = change;
  const short = reason.trim().length < MIN_REASON;
  const titles = { rename: `Rename the ${noun}`, retire: `Retire ${item.name}?`, restore: `Restore ${item.name}?` };
  const intro = {
    rename: `The new name shows everywhere this ${noun} is used, including on existing programmes, rules and profiles.`,
    retire: `It will no longer be offered for new programmes, eligibility rules or students' profiles. Everything that already uses it keeps it, and you can restore it later. Nothing is deleted.`,
    restore: `It will be offered again for new programmes, eligibility rules and students' profiles.`,
  };
  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissible={!busy}
      title={titles[kind]}
      description={intro[kind]}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" variant={kind === "retire" ? "danger" : "solid"} onClick={() => onSave({ name: name.trim(), reason: reason.trim() })} loading={busy} disabled={short || (kind === "rename" && !name.trim())}>
            {kind === "rename" ? "Save" : kind === "retire" ? `Retire ${item.name}` : "Restore"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {kind === "rename" && <Input label="Name" required maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />}
        <Textarea
          label="Reason"
          required
          rows={3}
          maxLength={1000}
          info={`At least ${MIN_REASON} characters. Kept in the ${noun}'s history, which cannot be changed.`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

/** Who changed the item, when, how and why: the append-only history. */
function HistoryDialog({ path, item, noun, onClose }) {
  const history = useQuery({
    queryKey: [...SETTINGS_KEY, path, item?.id, "history"],
    enabled: Boolean(item),
    queryFn: async () => (await scholarshipsApi.get(`admin/settings${path}/${item.id}/history`)).data,
  });
  return (
    <Modal open={Boolean(item)} onClose={onClose} size="lg" title={item ? `History of ${item.name}` : ""} description={`Every change to this ${noun}. The history cannot be changed or deleted.`}>
      {history.isPending ? (
        <Loading label="Loading the history…" />
      ) : history.isError ? (
        <EmptyState title="Could not load the history" onRefresh={history.refetch} />
      ) : (
        <ol className="flex flex-col gap-3">
          {history.data.map((h) => (
            <li key={h.id} className="rounded-md border border-slate-100 p-3 text-sm">
              <p className="font-semibold text-slate-800">
                {ACTIONS[h.action] ?? h.action}
                {h.action === "RENAMED" && `: ${h.fromName} → ${h.toName}`}
              </p>
              <p className="text-xs text-slate-500">
                {formatDateTime(h.occurredAt)}
                {h.actor?.name && ` · ${h.actor.name}`}
              </p>
              {h.reason && <p className="mt-1 italic text-slate-600">“{h.reason}”</p>}
            </li>
          ))}
        </ol>
      )}
    </Modal>
  );
}

/**
 * A list of names the Scholarship Admin adds to, renames, retires and restores. Nothing is
 * deleted: programmes, rules and profiles keep what they use, and every change has a reason.
 */
function NamedListTab({ path, noun, plural }) {
  const queryClient = useQueryClient();
  const items = useSetting(path);
  const [adding, setAdding] = useState("");
  const [change, setChange] = useState(null); // { kind: "rename" | "retire" | "restore", item }
  const [historyOf, setHistoryOf] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  if (items.isPending) return <Loading label={`Loading the ${plural}…`} />;
  if (items.isError) return <EmptyState title={`Could not load the ${plural}`} onRefresh={items.refetch} />;

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: SETTINGS_KEY });
    queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
  };
  const failed = (err, fallback) => (problemOf(err)?.code === "name-exists" ? `This ${noun} is already in the list.` : errorMessageOf(err, fallback));

  const add = async (e) => {
    e.preventDefault();
    if (!adding.trim()) return;
    setBusy(true);
    try {
      const { data } = await scholarshipsApi.post(`admin/settings${path}`, { name: adding.trim() });
      toast.success(`${data.name} added`);
      setAdding("");
      refresh();
    } catch (err) {
      console.error(`Failed to add the ${noun}:`, err);
      toast.error(failed(err, "Failed to add. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const save = async ({ name, reason }) => {
    setError(null);
    setBusy(true);
    const { kind, item } = change;
    try {
      if (kind === "rename") await scholarshipsApi.patch(`admin/settings${path}/${item.id}`, { name, reason });
      else await scholarshipsApi.post(`admin/settings${path}/${item.id}/${kind}`, { reason });
      toast.success(kind === "rename" ? "Renamed" : kind === "retire" ? `${item.name} retired` : `${item.name} restored`);
      setChange(null);
      refresh();
    } catch (err) {
      console.error(`Failed to ${kind} the ${noun} ${item.id}:`, err);
      setError(failed(err, "Failed to save. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const open = (kind, item) => {
    setError(null);
    setChange({ kind, item });
  };

  const columns = [
    { key: "name", label: "Name", accessor: (i) => i.name, render: (i) => <span className={i.retired ? "text-slate-400 line-through" : "font-medium text-slate-800"}>{i.name}</span> },
    { key: "status", label: "Status", accessor: (i) => (i.retired ? "Retired" : "In use"), render: (i) => (i.retired ? <Tag color="gray">Retired {formatDateTime(i.retiredAt)}</Tag> : <Tag color="green">Offered</Tag>) },
    {
      key: "actions",
      label: "",
      exportable: false,
      render: (i) => (
        <div className="flex justify-end gap-1">
          <RowAction Icon={ClockIcon} label="History" variant="ghost" onClick={() => setHistoryOf(i)} />
          <RowAction Icon={PencilSquareIcon} label="Rename" onClick={() => open("rename", i)} />
          {i.retired ? (
            <RowAction Icon={ArrowUturnLeftIcon} label="Restore" onClick={() => open("restore", i)} />
          ) : (
            <RowAction Icon={ArchiveBoxXMarkIcon} label="Retire" variant="ghost" onClick={() => open("retire", i)} />
          )}
        </div>
      ),
    },
  ];

  return (
    <Card subtitle={`Programmes, eligibility rules and students' profiles use these. A ${noun} is never deleted: retire it to stop offering it, and whatever already uses it keeps it. Every rename, retirement and restoration needs a reason and is kept in its history.`}>
      <div className="flex flex-col gap-4">
        <form onSubmit={add} className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <Input label={`New ${noun}`} maxLength={100} value={adding} onChange={(e) => setAdding(e.target.value)} className="sm:w-80" disabled={busy} />
          <Button type="submit" icon={<PlusIcon className="h-4 w-4" />} disabled={!adding.trim() || busy} loading={busy && Boolean(adding)}>
            Add
          </Button>
        </form>
        <Table columns={columns} data={items.data} rowKey={(i) => i.id} exportable={false} emptyTitle={`No ${plural} yet`} />
      </div>
      <ChangeDialog key={change ? `${change.kind}:${change.item.id}` : "none"} change={change} noun={noun} busy={busy} error={error} onClose={() => setChange(null)} onSave={save} />
      <HistoryDialog path={path} item={historyOf} noun={noun} onClose={() => setHistoryOf(null)} />
    </Card>
  );
}

// ------------------------------------------------------------------ the page

export default function SettingsPage() {
  const [tab, setTab] = useState("years");
  const currencies = useMemo(currencyCodes, []);

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title="Settings" subtitle="The lists the scholarship forms offer. Changes apply to new choices; values already saved stay as they are." />
      <Tabs tabs={TABS} value={tab} onChange={setTab} className="max-w-full overflow-x-auto" />
      {tab === "years" && <AcademicYearsTab />}
      {tab === "countries" && (
        <CodeChecklist path="/destination-countries" all={COUNTRY_CODES} labelOf={countryName} noun="countries" help="The countries a scholarship's destination can be." />
      )}
      {tab === "currencies" && (
        <CodeChecklist path="/currencies" all={currencies} labelOf={currencyName} noun="currencies" help="The currencies students ask for funding in, and HEC approves loans in." />
      )}
      {tab === "levels" && <NamedListTab path="/education-levels" noun="education level" plural="education levels" />}
      {tab === "fields" && <NamedListTab path="/fields-of-study" noun="field of study" plural="fields of study" />}
    </Container>
  );
}
